import React from 'react';
import { useNavigate } from 'react-router-dom';

// Per-error configuration — color, icon, copy
const CONFIGS = {
  400: {
    label: 'Error 400',
    title: 'Bad Request',
    description:
      'The server couldn\'t process this request. This is usually caused by invalid input or a malformed URL. Check your data and try again.',
    color: '#f59e0b',
    iconBg: 'rgba(245, 158, 11, 0.12)',
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  401: {
    label: 'Error 401',
    title: 'Session Expired',
    description:
      'Your session has ended or you are not currently authenticated. Please sign in again to continue where you left off.',
    color: '#8b5cf6',
    iconBg: 'rgba(139, 92, 246, 0.12)',
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    primaryAction: { label: 'Sign In', path: '/login' },
  },
  403: {
    label: 'Error 403',
    title: 'Access Denied',
    description:
      'You don\'t have permission to view this resource. If you believe this is a mistake, contact your system administrator to request access.',
    color: '#ef4444',
    iconBg: 'rgba(239, 68, 68, 0.10)',
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>
      </svg>
    ),
  },
  404: {
    label: 'Error 404',
    title: 'Page Not Found',
    description:
      'The page you\'re looking for doesn\'t exist or may have been moved to a different location. Double-check the URL or navigate back to safety.',
    color: '#3b82f6',
    iconBg: 'rgba(59, 130, 246, 0.10)',
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        <line x1="8" y1="11" x2="14" y2="11"/>
      </svg>
    ),
  },
  500: {
    label: 'Error 500',
    title: 'Server Error',
    description:
      'Something went wrong on our end and we\'re working to fix it. Please try refreshing the page or come back in a moment.',
    color: '#ef4444',
    iconBg: 'rgba(239, 68, 68, 0.10)',
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
        <path d="M9 8l1.5 1.5L12 8l1.5 1.5L15 8"/>
        <path d="M9 12h6"/>
      </svg>
    ),
  },
};

const ErrorPage = ({ code = 404, message }) => {
  const navigate = useNavigate();
  const config = CONFIGS[Number(code)] || CONFIGS[404];
  const { color, iconBg, icon, label, title, description, primaryAction } = config;
  const displayDesc = message || description;

  return (
    <div className="error-page-wrap">
      {/* Decorative background number */}
      <div className="error-page-bg-number">{code}</div>

      <div className="error-page-card">
        {/* Icon */}
        <div
          className="error-page-icon-wrap"
          style={{ background: iconBg, color }}
        >
          {icon}
        </div>

        {/* Code badge */}
        <div className="error-page-code-label" style={{ color }}>
          {label}
        </div>

        {/* Title */}
        <h1 className="error-page-title">{title}</h1>

        {/* Coloured rule */}
        <div className="error-page-rule" style={{ background: color }} />

        {/* Description */}
        <p className="error-page-desc">{displayDesc}</p>

        {/* Actions */}
        <div className="error-page-actions">
          <button
            className="btn btn-ghost"
            onClick={() => navigate(-1)}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M10 3L5 8l5 5"/>
            </svg>
            Go Back
          </button>

          {primaryAction ? (
            <button
              className="btn btn-primary"
              onClick={() => navigate(primaryAction.path)}
            >
              {primaryAction.label}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          )}

          {code === 500 && (
            <button
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                <path d="M13.5 4A6 6 0 1 0 14 9"/><path d="M14 2v3h-3" strokeLinejoin="round"/>
              </svg>
              Refresh Page
            </button>
          )}
        </div>

        {/* Footer note */}
        <div className="error-page-footer">
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="8" cy="8" r="7"/><line x1="8" y1="7" x2="8" y2="11"/><circle cx="8" cy="5" r="0.5" fill="currentColor"/>
          </svg>
          If this keeps happening, contact your administrator.
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
